import { Component, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ViewSettingsService } from '@navfleet/client/view-settings/domain/data-access';
import { ViewSettingsDefinition } from '@navfleet/client/view-settings/domain/models';
import { ViewSettingsComponent } from '@navfleet/client/view-settings/ui';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, map, skip } from 'rxjs';

@Component({
    selector: 'fl-view-settings-container',
    imports: [ViewSettingsComponent],
    templateUrl: './view-settings.container.html',
    styleUrls: ['./view-settings.container.scss'],
})
export class ViewSettingsContainer {
    public readonly service = inject(ViewSettingsService);
    private readonly definitionSubject = new BehaviorSubject<ViewSettingsDefinition | undefined>(undefined);

    public readonly definition = toSignal(this.definitionSubject.asObservable());

    constructor() {
        this.service.definition$
            .pipe(
                skip(1),
                map((definition) => this.onDefinitionChanged(definition)),
                takeUntilDestroyed()
            )
            .subscribe((definition) => this.definitionSubject.next(definition));
    }

    public onApply(): void {
        const definition = cloneDeep(this.definitionSubject.value);
        this.service.setDefinition(definition);
    }

    public onChange(change: { sectionKey: string; changedKey: string; newValue: boolean }): void {
        this.service.onViewSettingChanged(this.definitionSubject.value as ViewSettingsDefinition, change);
    }

    private onDefinitionChanged(definition?: ViewSettingsDefinition): ViewSettingsDefinition {
        const data = cloneDeep(definition);

        if (data) {
            Object.values(data.sections).forEach((s) =>
                s?.settings
                    ? Object.entries(s.settings)
                          .filter(([, v]) => v?.hidden)
                          .forEach(([k]) => delete s.settings[k])
                    : void 0
            );
        }

        return data as ViewSettingsDefinition;
    }
}
